// @flow

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { PROD_ENV } from '@alphaott/smart-tv-config'
import { KEYBOARD_ENTER_KEY_CODES } from '@alphaott/smart-tv-platforms/src/utils/isKeyboardEnterKeyCode'

import { getBrandTheme } from '../../core/brandTheme'
import { collectCrashContext, EMPTY_CRASH_CONTEXT } from './crashContext'
import { DEFAULT_CRASH_TEXTS, getCrashTexts } from './texts'
import { useReload } from './useReload'
import { useSendStatus } from './useSendStatus'
import { QRBoundary } from './QRBoundary'
import { getErrorType } from './utils/errorInfo'
import { buildServiceLines } from './utils/serviceLine'
import { buildPayload } from './utils/payload'
import {
  Container,
  Content,
  Icon,
  Title,
  Subtitle,
  ReloadButton,
  Support,
  SupportText,
  SupportLabel,
  SupportLine,
  QRBox,
  DevDetails,
} from './GlobalError.style'

const ENTER_KEY_CODES = Object.values(KEYBOARD_ENTER_KEY_CODES)

// Diagnostics are optional: a failure there hides only that part of the screen,
// the text and the reload button stay
const attempt = (fn: () => any, fallback: any): any => {
  try {
    return fn()
  } catch (e) {
    return fallback
  }
}

const DEFAULT_COLORS = { accent: '#e36d3a', accentText: '#fff' }

const isNonEmptyString = (value: mixed): boolean => typeof value === 'string' && value.length > 0

// primary.light/dark are not used: some brands still have template values there
const getBrandColors = () => {
  const { main, contrastText } = getBrandTheme().styledTheme.palette.primary

  return {
    accent: isNonEmptyString(main) ? main : DEFAULT_COLORS.accent,
    accentText: isNonEmptyString(contrastText) ? contrastText : DEFAULT_COLORS.accentText,
  }
}

const formatDevDetails = (error: any, componentStack: ?string): string =>
  [error && error.message, error && error.stack, componentStack]
    .filter((part) => typeof part === 'string' && part.length > 0)
    .join('\n\n')

type GlobalErrorProps = {
  error: mixed,
  componentStack?: ?string,
  eventId?: ?string,
}

// eslint-disable-next-line complexity
export const GlobalError = ({ error, componentStack, eventId }: GlobalErrorProps) => {
  const buttonRef = useRef(null)
  const reload = useReload()
  const [time] = useState(() => Date.now())
  const [{ language, texts, dir }] = useState(() => attempt(getCrashTexts, DEFAULT_CRASH_TEXTS))
  const [colors] = useState(() => attempt(getBrandColors, DEFAULT_COLORS))
  const [context] = useState(() => attempt(collectCrashContext, EMPTY_CRASH_CONTEXT))
  const id = typeof eventId === 'string' ? eventId : ''
  const sendStatus = useSendStatus(id)

  // No spatial navigation here: it may be broken by the crash. A plain button,
  // a click and Enter/OK on the document reload the app through one handler.
  useEffect(() => {
    attempt(() => buttonRef.current && buttonRef.current.focus(), null)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!ENTER_KEY_CODES.includes(event.keyCode)) return

      event.preventDefault()
      reload()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [reload])

  // Each part is built on its own: a failure hides only that part of the diagnostics
  const { serviceLines, payload } = useMemo(() => {
    const info = { ...context, eventId: id, sendStatus, time }

    return {
      serviceLines: attempt(
        () => buildServiceLines({ ...info, errorType: getErrorType(error) }),
        [],
      ),
      payload: attempt(() => buildPayload({ ...info, error, componentStack }), null),
    }
  }, [context, error, componentStack, id, sendStatus, time])

  const devDetails = PROD_ENV ? '' : attempt(() => formatDevDetails(error, componentStack), '')

  return (
    <Container>
      <Content dir={dir} lang={language}>
        <Icon
          $color={colors.accent}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </Icon>
        <Title>{texts.title}</Title>
        <Subtitle>{texts.subtitle}</Subtitle>
        <ReloadButton
          ref={buttonRef}
          type="button"
          onClick={reload}
          $background={colors.accent}
          $color={colors.accentText}
        >
          {texts.reload}
        </ReloadButton>
      </Content>

      {(serviceLines.length > 0 || payload) && (
        <Support>
          {serviceLines.length > 0 && (
            <SupportText>
              <SupportLabel dir={dir} lang={language}>
                {texts.support}
              </SupportLabel>
              {/* IDs, versions and times stay left-to-right in every language */}
              {serviceLines.map((line) => (
                <SupportLine key={line} dir="ltr">
                  {line}
                </SupportLine>
              ))}
            </SupportText>
          )}
          {payload && (
            <QRBoundary>
              <QRBox>
                <QRCodeSVG value={payload} level="L" includeMargin />
              </QRBox>
            </QRBoundary>
          )}
        </Support>
      )}

      {devDetails && <DevDetails dir="ltr">{devDetails}</DevDetails>}
    </Container>
  )
}

export default GlobalError
